import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth } from '~/lib/auth-context'
import { useEffect, useRef } from 'react'

const SUPPORTED_LOCALES = ['es', 'gl', 'en', 'de', 'fr', 'it', 'nl', 'pt']

export const Route = createFileRoute('/$locale/')({
  component: AuthIndexPage,
})

function AuthIndexPage() {
  const { locale } = Route.useParams()
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()
  const hasRedirected = useRef(false)
  const renderCountRef = useRef(0)

  // Sanitize locale — if $locale is not a real locale (e.g. '/sign-in' matched as locale),
  // fall back to 'en' (B-new-1008 — unified default locale) so redirects never
  // propagate the bad value.
  const safeLocale = SUPPORTED_LOCALES.includes(locale) ? locale : 'en'

  // Track renders to detect infinite loops
  renderCountRef.current++
  if (renderCountRef.current > 50) {
    console.error('AuthIndexPage redirect loop detected, stopping...')
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Redirect Loop Detected</h1>
          <p className="text-gray-400">Please refresh the page or try signing in directly.</p>
          <a
            href={`/${safeLocale}/sign-in`}
            className="inline-block mt-4 px-6 py-3 bg-[#00f0ff] text-[#0a0a0f] rounded-lg hover:bg-[#39ff14] font-mono"
          >
            Go to Sign In
          </a>
        </div>
      </div>
    )
  }

  // Redirect based on auth status - use React Router's navigate instead of window.location
  useEffect(() => {
    if (!isLoading && !hasRedirected.current) {
      hasRedirected.current = true
      if (user) {
        // Authenticated users go to job search
        navigate({ to: '/$locale/jobs', params: { locale: safeLocale } })
      } else {
        // Unauthenticated users go to sign-in
        navigate({ to: '/$locale/sign-in', params: { locale: safeLocale }, search: { error: undefined } })
      }
    }
  }, [user, isLoading, safeLocale, navigate])

  // Show loading while auth loads
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[#00f0ff] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#a0a0b0] font-mono text-sm">LOADING...</p>
      </div>
    </div>
  )
}
